import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { AUTH_CONFIG } from "../../config/authConfig";
import EmailLoginForm from "./EmailLoginForm";
import RegisterForm from "./RegisterForm";
import MobileOtpForm from "./MobileOtpForm";

export default function AuthModal({ isOpen, onClose }) {
  const [mode, setMode] = useState(AUTH_CONFIG.defaultMode); // "email" | "mobile"
  const [authType, setAuthType] = useState("login");         // "login" | "register"
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) requestAnimationFrame(() => setVisible(true));
    else setVisible(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 220);
  };

  const showEmailTab  = AUTH_CONFIG.enableEmailLogin;
  const showMobileTab = AUTH_CONFIG.enableMobileOtp;
  const showTabs      = showEmailTab && showMobileTab;

  return (
    <>
      {/* ── spinner keyframe injected once ── */}
      <style>{`@keyframes _spin{to{transform:rotate(360deg)}} @keyframes _up{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:none}}`}</style>

      {/* ── BACKDROP ── */}
      <div
        onClick={(e) => e.target === e.currentTarget && handleClose()}
        style={{
          position: "fixed", inset: 0, zIndex: 50,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "16px",
          background: visible ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0)",
          backdropFilter: visible ? "blur(4px)" : "blur(0px)",
          transition: "background .22s ease, backdrop-filter .22s ease",
        }}
      >
        {/* ── CARD ── */}
        <div style={{
          position: "relative",
          background: "#fff",
          width: "100%",
          maxWidth: "440px",          /* single-column, fits any screen */
          borderRadius: "20px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
          padding: "32px 28px 28px",
          opacity: visible ? 1 : 0,
          transform: visible ? "none" : "translateY(16px) scale(.97)",
          transition: "opacity .22s ease, transform .22s ease",
          boxSizing: "border-box",
        }}>

          {/* ── CLOSE ── */}
          <button
            onClick={handleClose}
            style={{
              position: "absolute", top: 14, right: 14,
              background: "none", border: "none", cursor: "pointer",
              color: "#9ca3af", padding: "4px", borderRadius: "6px",
              display: "flex", alignItems: "center", transition: "color .15s",
            }}
            onMouseEnter={e => e.currentTarget.style.color = "#111"}
            onMouseLeave={e => e.currentTarget.style.color = "#9ca3af"}
          >
            <X size={18} />
          </button>

          {/* ── HEADING ── */}
          <h2 style={{ fontSize: "20px", fontWeight: 600, color: "#111827", margin: "0 0 4px" }}>
            {mode === "email"
              ? (authType === "login" ? "Sign in" : "Create account")
              : "Sign in"}
          </h2>
          <p style={{ fontSize: "13px", color: "#9ca3af", margin: "0 0 22px" }}>
            {mode === "email"
              ? (authType === "login" ? "Welcome back" : "Join us today")
              : "We'll send a one-time password"}
          </p>

          {/* ── TABS ── */}
          {showTabs && (
            <div style={{
              display: "flex",
              border: "1.5px solid #e5e7eb",
              borderRadius: "12px",
              overflow: "hidden",
              marginBottom: "24px",
            }}>
              {showEmailTab && (
                <TabBtn
                  active={mode === "email"}
                  onClick={() => { setMode("email"); setAuthType("login"); }}
                  first
                >
                  Email
                </TabBtn>
              )}
              {showMobileTab && (
                <TabBtn
                  active={mode === "mobile"}
                  onClick={() => setMode("mobile")}
                >
                  Mobile OTP
                </TabBtn>
              )}
            </div>
          )}

          {/* ── EMAIL FORMS ── */}
          {mode === "email" && showEmailTab && (
            <>
              {authType === "login"
                ? <EmailLoginForm onSuccess={handleClose} />
                : <RegisterForm   onSuccess={() => setAuthType("login")} />
              }

              {AUTH_CONFIG.enableRegister && (
                <p style={{ textAlign: "center", fontSize: "13px", color: "#6b7280", marginTop: "18px" }}>
                  {authType === "login" ? (
                    <>New here?{" "}
                      <InlineBtn onClick={() => setAuthType("register")}>Create account</InlineBtn>
                    </>
                  ) : (
                    <>Already have an account?{" "}
                      <InlineBtn onClick={() => setAuthType("login")}>Sign in</InlineBtn>
                    </>
                  )}
                </p>
              )}
            </>
          )}

          {/* ── MOBILE OTP FORM ── */}
          {mode === "mobile" && showMobileTab && (
            <MobileOtpForm onSuccess={handleClose} inModal />
          )}

        </div>
      </div>
    </>
  );
}

function TabBtn({ active, onClick, children, first }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        height: "40px",
        fontSize: "13px",
        fontWeight: 500,
        border: "none",
        borderLeft: first ? "none" : "1.5px solid #e5e7eb",
        cursor: "pointer",
        transition: "background .15s, color .15s",
        background: active ? "#2563eb" : "#fff",
        color:      active ? "#fff"     : "#374151",
        letterSpacing: "0.01em",
      }}
    >
      {children}
    </button>
  );
}

function InlineBtn({ onClick, children }) {
  return (
    <button onClick={onClick} style={{
      background: "none", border: "none", cursor: "pointer",
      color: "#2563eb", fontSize: "13px", fontWeight: 500,
      padding: 0, textDecoration: "underline", textUnderlineOffset: "2px",
    }}>
      {children}
    </button>
  );
}